<script lang="ts" setup>
import { CallHistory } from "~/types";
import { ElNotification } from "element-plus";
import { ElementUIFormRef } from "~/types/element-ui/ElementUIFormRef";
import { useConfigStore } from "~/stores/configStore";
import { PermissionNames } from "~/configs/permission_names";
import { CallOrigin } from "~/types/enums/callOrigin";
import { useCallHistoriesStore } from "~/projects/cc/stores/callHistorieStore";
import { useDialogStore } from "~/projects/cc/stores/dialogStore";

const configStore = useConfigStore();
const dialogStore = useDialogStore();
const callHistoriesStore = useCallHistoriesStore();
const formData = ref<CallHistory>(callHistoriesStore.selectedHistoryForEdit);
const props = defineProps({
  showEditModalHeader: {
    type: Boolean,
    default: true,
  },
  remoteEdit: {
    type: Boolean,
    default: false,
  },
});
const formRef = ref<ElementUIFormRef | null>(null);
onBeforeMount(async () => {
  setCategoryOptions();
  setSubcategoryOptions();
  setSubSubcategoryOptions();
  setClosingStatus();
});
const size = ref("small");
const rules = reactive({});

const call_category_options = ref();
const call_subcategory_options = ref();
const call_sub_subcategory_options = ref();
const closing_status_options = ref();

const setCategoryOptions = () => {
  const categories = configStore.globalConfig.call_history_categories.filter(
    (cc) => cc.parent_id === null,
  );
  call_category_options.value = categories;
};

const setSubcategoryOptions = () => {
  if (formData.value.cat_id) {
    const subCategories =
      configStore.globalConfig.call_history_categories.filter(
        (cc) => cc.parent_id === formData.value.cat_id,
      );
    call_subcategory_options.value = subCategories;
    const subcat_id = subCategories.find(
      (sc) => sc.id === formData.value.subcat_id,
    );
    if (!subcat_id) {
      formData.value.subcat_id = null;
      formData.value.subsubcat_id = null;
    }
  }
  setClosingStatus();
};
const setSubSubcategoryOptions = () => {
  if (formData.value.subcat_id) {
    const subSubCategories =
      configStore.globalConfig.call_history_categories.filter(
        (cc) => cc.parent_id === formData.value.subcat_id,
      );
    call_sub_subcategory_options.value = subSubCategories;
    const subsubcat_id = subSubCategories.find(
      (ssc) => ssc.id === formData.value.subsubcat_id,
    );
    if (!subsubcat_id) {
      formData.value.subsubcat_id = null;
    }
  }
};

const setClosingStatus = () => {
  if (formData.value.cat_id) {
    const closing_statuses =
      configStore.globalConfig.call_closing_statuses.filter(
        (ccs) => ccs.category_id === formData.value.cat_id,
      );
    closing_status_options.value = closing_statuses;
    const cs_id = closing_statuses.find((cs) => cs.id === formData.value.cs_id);
    if (!cs_id) {
      formData.value.cs_id = null;
    }
  }
};

const submitForm = function () {
  if (!props.remoteEdit) {
    if (!formRef.value) return;
    formRef.value.validate(async (valid: boolean) => {
      if (!valid) return;
      const history = await callHistoriesStore.updateHistory(formData.value);

      if (history) {
        ElNotification({
          type: "success",
          title: "Updated",
          message: "History updated",
        });
      }
      callHistoriesStore.setEditModalVisibility(false);
      await callHistoriesStore.getCallHistories();
    });
  } else {
    dialogStore.addNote(formData.value);
  }
};
const emit = defineEmits(["close"]);
const close = () => {
  callHistoriesStore.setEditModalVisibility(false);
  emit("close");
  formData.value = {};
};
onMounted(() => {
  if (
    props.remoteEdit &&
    dialogStore.ongoingCallOrigin === CallOrigin.TeleSales &&
    dialogStore.teleSaleInfo.teleSale.cat_id
  ) {
    formData.value.cat_id = dialogStore.teleSaleInfo.teleSale.cat_id;
    setCategoryOptions();
    setSubcategoryOptions();
    setSubSubcategoryOptions();
    setClosingStatus();
    if (dialogStore.teleSaleInfo.teleSale.remarks) {
      formData.value.remarks = dialogStore.teleSaleInfo.teleSale.remarks;
    }
  }
});
</script>
<template>
  <EditModalHeader v-if="props.showEditModalHeader">
    <template #title> Add Information</template>
    <template #svg>
      <Svgs name="pen" />
    </template>
  </EditModalHeader>
  <el-form
    ref="formRef"
    :model="formData"
    :rules="rules"
    class="space-y-5"
    @submit.prevent="submitForm"
  >
    <el-form-item class="" prop="remarks">
      <div class="form-elem-container">
        <FormLabel :required="true"> Remarks</FormLabel>
        <el-input v-model="formData.remarks" :size="size" type="textarea" />
      </div>
    </el-form-item>
    <el-form-item prop="call_category" class="w-full">
      <div class="form-elem-container">
        <FormLabel>Call Category</FormLabel>
        <el-select
          v-model="formData.cat_id"
          filterable
          class="w-full"
          placeholder="Select Call Category"
          @change="setSubcategoryOptions"
        >
          <el-option
            v-for="item in call_category_options"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </div>
    </el-form-item>
    <el-form-item prop="call_subcategory" class="w-full">
      <div class="form-elem-container">
        <FormLabel>Call Subcategory</FormLabel>
        <el-select
          v-model="formData.subcat_id"
          filterable
          class="w-full"
          placeholder="Select Call Subcategory"
          @change="setSubSubcategoryOptions"
        >
          <el-option
            v-for="item in call_subcategory_options"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </div>
    </el-form-item>
    <el-form-item prop="call_sub_subcategory" class="w-full">
      <div class="form-elem-container">
        <FormLabel>Call Sub Subcategory</FormLabel>
        <el-select
          v-model="formData.subsubcat_id"
          filterable
          class="w-full"
          placeholder="Select Call Sub Subcategory"
        >
          <el-option
            v-for="item in call_sub_subcategory_options"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </div>
    </el-form-item>
    <el-form-item prop="cs_id" class="w-full">
      <div class="form-elem-container">
        <FormLabel>Call Closing Status</FormLabel>
        <el-select
          v-model="formData.cs_id"
          filterable
          class="w-full"
          placeholder="Select Call Closing Status"
        >
          <el-option
            v-for="item in closing_status_options"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </div>
    </el-form-item>
    <el-form-item prop="brand_id" class="w-full">
      <div class="form-elem-container">
        <FormLabel>Brand Name</FormLabel>
        <el-select
          v-model="formData.brand_id"
          filterable
          class="w-full"
          placeholder="Select Brand Name"
        >
          <el-option
            v-for="item in configStore.globalConfig.call_brand_names"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </div>
    </el-form-item>
    <div class="w-full flex gap-2 items-center justify-end">
      <NavButton @click="close"> Cancel</NavButton>
      <NavButton
        class="bg-light-purple-nav"
        type="submit"
        :is-disabled="!isUserPermitted(PermissionNames.CALL_HISTORY_UPDATE)"
      >
        Update
      </NavButton>
    </div>
  </el-form>
</template>
